<script setup lang="ts">
import RelatedGames from '../tournament/RelatedGames.vue';
import { useMissionCard } from '~/composables/useMissionCard';

const props = defineProps<{
  modalVisible: boolean;
  mission: TMissionOrBadge;
  zIndex?: number;
  showParticipateButton?: boolean;
}>();

// Modal de termos é aninhado, então sobe 1 acima do principal pra não cair
// atrás dele quando o consumidor passa um zIndex customizado.
const termsZIndex = computed(() =>
  props.zIndex ? props.zIndex + 1 : undefined,
);

const relatedGamesZIndex = computed(() =>
  props.zIndex ? props.zIndex + 1 : undefined,
);

const emit = defineEmits(['update:modalVisible']);

const { isDesktop } = useLayout();

const isModalVisible = computed({
  get: () => props.modalVisible,
  set: (value) => emit('update:modalVisible', value),
});

const isTermsModalVisible = ref(false);
const isRelatedGamesListModalVisible = ref(false);

const missionCardProps = reactive({
  get mission() {
    return props.mission;
  },
});

const {
  isRelatedGamesModalVisible,
  formattedHintText,
  rewardData,
  formattedRewardText,
  timer,
  buttonState,
  handleButtonClick,
  isOptInRequired,
} = useMissionCard(missionCardProps, () => {});

// Verifica se tem jogos relacionados
const hasRelatedGames = computed(() => {
  return props.mission.related_games && props.mission.related_games.length > 0;
});

// Quantidade máxima de jogos a exibir inline
const maxInlineGames = 5;

// Verifica se o timer está zerado
const isTimerZero = computed(() => {
  return (
    timer.days === '00' &&
    timer.hours === '00' &&
    timer.minutes === '00' &&
    timer.seconds === '00'
  );
});

// Verifica se a missão está expirada
const isExpired = computed(() => {
  const currentTime = new Date().getTime();

  if (props.mission.dt_start && props.mission.time_limit_ms) {
    return currentTime > props.mission.dt_start + props.mission.time_limit_ms;
  }

  if (props.mission.active_till_ts) {
    return currentTime > props.mission.active_till_ts;
  }

  return false;
});

// Verifica se a missão já foi claimada (resgatada)
const isClaimed = computed(() => {
  return !!props.mission.prize_claimed_date_ts;
});

// Mostra o timer apenas se não estiver expirado, o timer não estiver zerado e a missão não foi claimada
const showTimer = computed(() => {
  return !isExpired.value && !isTimerZero.value && !isClaimed.value;
});
</script>

<template>
  <BaseModalMissionModal
    v-model="isModalVisible"
    :z-index="zIndex"
  >
    <template #header>
      <div
        v-if="!isDesktop"
        class="flex flex-col items-start gap-2"
      >
        <p class="text-[13px] leading-[20px] font-bold text-white">{{
          mission.name
        }}</p>
        <p class="text-white/50 font-normal text-[10px] leading-[15px]">
          {{ mission.description }}
        </p>
      </div>
      <div
        v-else
        class="flex items-center gap-3"
      >
        <NuxtImg
          v-if="mission.image"
          alt="Mission Image"
          class="max-h-[110px] object-contain"
          :draggable="false"
          :src="mission.image"
        />
        <div class="flex-1">
          <p class="text-[13px] leading-[20px] font-bold text-white mb-2">
            {{ mission.name }}
          </p>
          <p class="text-white/50 font-normal text-[10px] leading-[15px]">
            {{ mission.description }}
          </p>
        </div>
      </div>
    </template>
    <div class="text-white font-bold px-2.5 py-1.5 rounded-[10px] bg-white/5">
      <div class="flex items-center justify-between w-full mb-1.5">
        <p class="text-[16px] font-bold flex-shrink-0">Tarefas</p>
        <!-- Jogos Relacionados ao lado de Tarefas -->
        <div
          v-if="hasRelatedGames"
          class="flex items-center gap-1.5 cursor-pointer hover:brightness-110 transition-all bg-white/10 rounded-full py-1 px-2.5"
          @click="isRelatedGamesListModalVisible = true"
        >
          <div class="flex items-center">
            <div
              v-for="(related, index) in mission.related_games?.slice(
                0,
                maxInlineGames,
              )"
              :key="index"
              class="w-[20px] h-[20px] rounded-full overflow-hidden flex-shrink-0 border border-white/20"
              :class="index > 0 ? '-ml-[8px]' : ''"
            >
              <NuxtImg
                class="object-cover w-full h-full"
                :src="related?.game_public_meta?.image"
              />
            </div>
          </div>
          <span class="text-white/70 text-[10px] font-medium whitespace-nowrap"
            >Jogos Elegíveis</span
          >
        </div>
      </div>
      <p class="mb-1.5 text-[12px] font-normal text-white/60">
        Participe dos jogos elegíveis e complete as tarefas abaixo para coletar
        o seu bônus de missão</p
      >
      <p
        v-for="task in mission.tasks"
        :key="task.id"
        :class="`flex mb-[6px] rounded-[4px] gap-2 items-center justify-start p-[4px] px-[5px] border-[0.519px] ${task.is_completed ? 'border-[rgba(0,201,255,0.35)]' : 'border-[#767676]'}`"
        :style="{
          background: task.is_completed
            ? 'rgba(0, 201, 255, 0.28)'
            : 'linear-gradient(90deg, rgba(229, 229, 229, 0.23) 0%, rgba(127, 127, 127, 0.05) 100%)',
        }"
      >
        <NuxtIcon
          class="text-[10px] font-extrabold rounded-full p-[3px]"
          :class="
            task.is_completed
              ? 'bg-[#00C9FF] text-white'
              : 'bg-white/50 text-white'
          "
          :name="task.is_completed ? 'check' : 'close'"
        />
        <span class="text-[#ECECFC] font-normal text-[11.4px]">
          {{ task.name }}
        </span>
      </p>
    </div>
    <div
      class="text-white font-bold p-2.5 pb-3 rounded-[10px] bg-white/5 mt-[12px]"
    >
      <p class="mb-2.5 text-[16px] font-bold text-center">🎁 Recompensas</p>
      <div
        class="flex justify-center items-center gap-[10.384px] self-stretch [background:rgba(0,201,255,0.05)] p-[10.384px] rounded-[10.384px] border-[1.038px] border-dashed border-[#00C9FF]"
      >
        <NuxtImg
          v-if="rewardData.image"
          alt="Reward Image"
          class="h-[30px] object-contain"
          :src="rewardData.image"
        />
        <span
          v-if="rewardData.text"
          class="w-[227.412px] text-white text-[11.423px] font-normal leading-[17.653px]"
          v-html="formattedRewardText"
        ></span>
        <span
          v-else
          class="text-white text-[11.423px] font-normal leading-[17.653px] text-center"
        >
          {{ rewardData.reward }}
        </span>
      </div>
    </div>

    <div
      v-if="showTimer"
      class="flex h-6 mt-3 justify-center items-center gap-[5.887px] [background:rgba(198,21,21,0.23)] rounded-[5.887px]"
    >
      <p class="text-white text-[11.775px] font-medium leading-[14.13px]"
        >Essa missão acaba em:</p
      >
      <NuxtIcon
        class="text-white text-[16px]"
        name="mission/timer-acaba-em"
      />
      <p class="text-white text-[16.485px] font-extrabold leading-[21.195px]">
        {{ Number(timer?.days) > 0 ? `${timer.days}d` : '' }}
        {{ timer.hours }}:{{ timer.minutes }}:<span
          style="display: inline-block; width: 20px; text-align: center"
          >{{ timer.seconds }}</span
        >
      </p>
    </div>
    <button
      v-if="showParticipateButton && buttonState && isOptInRequired"
      :class="['mt-3 w-full !h-[30px]', buttonState.class]"
      :disabled="buttonState.disabled"
      @click="handleButtonClick"
    >
      <NuxtIcon
        :filled="true"
        :name="buttonState.iconName"
      />
      <span :class="buttonState.textClass">{{ buttonState.text }}</span>
    </button>
    <div
      v-else-if="showParticipateButton && buttonState"
      :class="['mt-3 w-full !h-[30px] cursor-default', buttonState.class]"
    >
      <span :class="buttonState.textClass">{{ buttonState.text }}</span>
    </div>
    <div
      v-if="mission.hint_text"
      class="flex items-center justify-center gap-1 mt-3 text-[13px] text-center mb-2 text-white/50 w-full rounded-full h-[26px] px-[10px] py-[5px] bg-white/5 hover:brightness-75 cursor-pointer"
      @click="isTermsModalVisible = true"
    >
      <span class="font-medium text-[9px] block text-center"
        >Termos e condições</span
      >
      <NuxtIcon name="mission/circle-exclamation" />
    </div>
    <!-- termos e condicoes -->
    <BaseModalMissionModal
      v-model="isTermsModalVisible"
      :z-index="termsZIndex"
    >
      <template #header>
        <NuxtIcon
          class="text-xl text-white"
          name="mission/info"
        />
        <p class="text-lg font-bold text-white">Termos e condições</p>
      </template>
      <div v-if="mission.hint_text">
        <p
          v-for="(line, index) in formattedHintText"
          :key="index"
          class="mb-2 text-white/50 font-normal text-[13px]"
        >
          {{ line }}
        </p>
      </div>
    </BaseModalMissionModal>

    <RelatedGames
      v-model:modal-visible="isRelatedGamesModalVisible"
      :is-loading="false"
      :items="mission.related_games"
      style="display: none"
      title="Jogos Elegíveis"
      :z-index="relatedGamesZIndex"
    />

    <!-- Modal de lista de jogos relacionados -->
    <RelatedGames
      v-model:modal-visible="isRelatedGamesListModalVisible"
      :is-loading="false"
      :items="mission.related_games"
      style="display: none"
      title="Jogos Elegíveis"
      :z-index="relatedGamesZIndex"
    />
  </BaseModalMissionModal>
</template>
